/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-shadow */
import type React from 'react'
import { ResourceFromSDK } from 'TypeDefs/Contentful'
import {
  AiBlocksUnion,
  AssetType,
  Campaign,
  ColorTheme,
  DonationInterval,
  NonprofitExperienceRange,
  NonprofitOrgDonationsState,
  NonprofitOrgRole,
  PaidStaffSizeRange,
  VolunteerSizeRange,
} from 'Generated/graphql-types'
import { FormationPlan, PlatformPlan } from './Checkout'
import { GooglePlaceApiPayload } from './Settings'

export const UPDATE_PROGRESS = 'UPDATE_PROGRESS'
export const UPDATE_DISPLAY_PROGRESS = 'UPDATE_DISPLAY_PROGRESS'
export const UPDATE_HEADER = 'UPDATE_HEADER'
export const UPDATE_DISPLAY_UPLOAD_BUTTON = 'UPDATE_DISPLAY_UPLOAD_BUTTON'
export const UPDATE_NONPROFIT_ORG_CONFIG = 'UPDATE_ORG_CONFIG'
export const UPDATE_NONPROFIT_DONATION_DETAILS =
  'UPDATE_NONPROFIT_DONATION_DETAILS'
export const UPDATE_CAMPAIGNS = 'UPDATE_CAMPAIGNS'
export const UPDATE_CAMPAIGNS_FILTER_DATE = 'UPDATE_CAMPAIGNS_FILTER_DATE'
export const UPDATE_FUNDER_ORG_CONFIG = 'UPDATE_FUNDER_ORG_CONFIG'
export const UPDATE_USER_CONFIG = 'UPDATE_USER_CONFIG'
export const UPDATE_DISPLAY_BACK_NAV = 'UPDATE_DISPLAY_BACK_NAV'
export const UPDATE_BOX_ACCESS_TOKEN = 'UPDATE_BOX_ACCESS_TOKEN'
export const UPDATE_DISPLAY_SECURE_CHECKOUT = 'UPDATE_DISPLAY_SECURE_CHECKOUT'
export const UPDATE_DISPLAY_LOGIN_BUTTON = 'UPDATE_DISPLAY_LOGIN_BUTTON'
export const UPDATE_NONPROFIT_ORG_CONFIG_LOADING =
  'UPDATE_NONPROFIT_ORG_LOADING'
export const UPDATE_FUNDER_ORG_CONFIG_LOADING =
  'UPDATE_FUNDER_ORG_CONFIG_LOADING'
export const UPDATE_NONPROFIT_ORG_CONFIG_ERRORED =
  'UPDATE_NONPROFIT_ORG_ERRORED'
export const UPDATE_FUNDER_ORG_CONFIG_ERRORED =
  'UPDATE_FUNDER_ORG_CONFIG_ERRORED'
export const UPDATE_USER_CONFIG_LOADING = 'UPDATE_USER_CONFIG_LOADING'
export const UPDATE_DISPLAY_GRANTEES_DROPDOWN =
  'UPDATE_DISPLAY_GRANTEES_DROPDOWN'
export const UPDATE_CLOUDINARY_CONFIG = 'UPDATE_CLOUDINARY_CONFIG'
export const UPDATE_SHOW_NAVIGATION = 'UPDATE_SHOW_NAVIGATION'
export const UPDATE_APP_PANEL = 'UPDATE_APP_PANEL'
export const UPDATE_FEATURE_GATING_LIMIT = 'UPDATE_FEATURE_GATING_LIMIT'
export const UPDATE_SHOW_ACADEMY_INTRO_SECTION =
  'UPDATE_SHOW_ACADEMY_INTRO_SECTION'
export const UPDATE_DISMISSED_RESOURCES = 'UPDATE_DISMISSED_RESOURCES'
export const UPDATE_ADBLOCKER_DETECTED = 'UPDATE_ADBLOCKER_DETECTED'
export const UPDATE_THIRD_PARTY_NONPROFIT_ORG_CONFIG =
  'UPDATE_THIRD_PARTY_NONPROFIT_ORG_CONFIG'
export const UPDATE_THIRD_PARTY_NONPROFIT_ORG_CONFIG_LOADING =
  ' UPDATE_THIRD_PARTY_NONPROFIT_ORG_CONFIG_LOADING'
export const UPDATE_FEATURE_FLAGS = 'UPDATE_FEATURE_FLAGS'
export const UPDATE_FEATURE_FLAGS_LOADING = 'UPDATE_FEATURE_FLAGS_LOADING'
export const UPDATE_GLOBAL_DIALOG = 'UPDATE_GLOBAL_DIALOG'
export const SET_BLOCK_DRAGGING = 'SET_BLOCK_DRAGGING'
export const SET_STORY_DETAILS = 'SET_STORY_DETAILS'
export const SET_ACTIVE_SIDE_PANEL = 'SET_ACTIVE_SIDE_PANEL'
export const SET_TOC_VISIBILITY = 'SET_TOC_VISIBILITY'
export const SET_REDIRECT_PATH = 'SET_REDIRECT_PATH'
export const SET_AI_STORY_CONTENT = 'SET_AI_STORY_CONTENT'

type UpdateBoxToken = {
  type: typeof UPDATE_BOX_ACCESS_TOKEN
  tokenData: TokenData
}
type UpdateProgressAction = {
  type: typeof UPDATE_PROGRESS
  linearProgress: number
}

type UpdateDisplayProgressAction = {
  type: typeof UPDATE_DISPLAY_PROGRESS
  displayLinearProgress: boolean
}

type UpdateHeaderAction = {
  type: typeof UPDATE_HEADER
  text: string
  iconName: string
}

type UpdateNonprofitOrgConfig = {
  type: typeof UPDATE_NONPROFIT_ORG_CONFIG
  nonprofitOrgConfig: NonprofitOrgConfig
}
type UpdateNonprofitDonationDetails = {
  type: typeof UPDATE_NONPROFIT_DONATION_DETAILS
  donationDetails: NonprofitDonationDetails
}

type UpdateCampaigns = {
  type: typeof UPDATE_CAMPAIGNS
  payload: {
    data: Campaign[]
    isLoading: boolean
  }
}

type UpdateCampaignsFilterDate = {
  type: typeof UPDATE_CAMPAIGNS_FILTER_DATE
  filterDate: number | null
}

type UpdateNonprofitLoading = {
  type: typeof UPDATE_NONPROFIT_ORG_CONFIG_LOADING
  loading: boolean
}
type UpdateFunderLoading = {
  type: typeof UPDATE_FUNDER_ORG_CONFIG_LOADING
  loading: boolean
}

type UpdateNonprofitErrored = {
  type: typeof UPDATE_NONPROFIT_ORG_CONFIG_ERRORED
  errored: boolean
}
type UpdateFunderErrored = {
  type: typeof UPDATE_FUNDER_ORG_CONFIG_ERRORED
  errored: boolean
}

type UpdateUserLoading = {
  type: typeof UPDATE_USER_CONFIG_LOADING
  loading: boolean
}

type UpdateFunderOrgConfig = {
  type: typeof UPDATE_FUNDER_ORG_CONFIG
  funderOrgConfig: FunderOrgConfig
}

type UpdateUserConfig = {
  type: typeof UPDATE_USER_CONFIG
  userConfig: UserConfig
}

type UpdateCloudinaryConfig = {
  type: typeof UPDATE_CLOUDINARY_CONFIG
  cloudinaryConfig: CloudinaryConfig
}

type UpdateAppDrawer = {
  type: typeof UPDATE_SHOW_NAVIGATION
  showNavigation: boolean
}

type UpdateAppPanel = {
  type: typeof UPDATE_APP_PANEL
  appPanel: boolean
}

type UpdateFeatureGatingLimit = {
  type: typeof UPDATE_FEATURE_GATING_LIMIT
  featureGatingLimit: FeatureGatingLimit[]
}

type UpdateShowAcademyIntroSection = {
  type: typeof UPDATE_SHOW_ACADEMY_INTRO_SECTION
  showAcademyIntroSection: boolean
}

type UpdateDismissedResources = {
  type: typeof UPDATE_DISMISSED_RESOURCES
  dismissedResources: ResourceFromSDK
}

type UpdateAdblockerDetected = {
  type: typeof UPDATE_ADBLOCKER_DETECTED
  adblockerDetected: boolean
}

type UpdateThirdPartyNonprofitOrgConfig = {
  type: typeof UPDATE_THIRD_PARTY_NONPROFIT_ORG_CONFIG
  nonprofitOrgConfig: ThirdPartyNonprofitOrgConfig
}

type UpdateFeatureFlagsLoading = {
  type: typeof UPDATE_FEATURE_FLAGS_LOADING
  loading: boolean
}

export enum DialogVariant {
  TRIAL_END = 'trial-end',
  UPGRADE_MODAL_DIALOG = 'upgrade-modal-dialog',
}

interface DialogPayload<V extends DialogVariant> {
  isVisible: boolean
  variant: V | null
}

interface TrialEndPayload extends DialogPayload<DialogVariant.TRIAL_END> {
  url: string
}

type UpgradeModalDialogPayload =
  DialogPayload<DialogVariant.UPGRADE_MODAL_DIALOG>

type UpdateGlobalDialog<T> = {
  type: typeof UPDATE_GLOBAL_DIALOG
  payload: T
}

type UpdateFeatureFlags = {
  type: typeof UPDATE_FEATURE_FLAGS
  activeFeatureFlags: string[]
}

type SetBlockDragging = {
  type: typeof SET_BLOCK_DRAGGING
  payload: boolean
}

type SetRedirectPath = {
  type: typeof SET_REDIRECT_PATH
  payload: { redirectPath: string }
}

type SetStoryDetails = {
  type: typeof SET_STORY_DETAILS
  payload: {
    showTableOfContents?: boolean
    showHeader?: boolean
    title?: string
    socialDescription?: string
    socialTitle?: string
    colorTheme?: ColorTheme
    id?: number
    hashedId?: string
    isSharedWithFunder?: boolean
  }
}

type SetActiveSidePanel = {
  type: typeof SET_ACTIVE_SIDE_PANEL
  payload: 'NONE' | 'SETTINGS' | 'IMAGE' | 'PROGRESS_REPORT'
}

type SetTocVisibility = {
  type: typeof SET_TOC_VISIBILITY
  payload: boolean
}

type SetAiStoryContent = {
  type: typeof SET_AI_STORY_CONTENT
  payload: {
    blocks: AiBlocksUnion[] | null
    template: AssetType[] | null
  }
}

export type Action =
  | UpdateProgressAction
  | UpdateDisplayProgressAction
  | UpdateHeaderAction
  | UpdateNonprofitOrgConfig
  | UpdateNonprofitDonationDetails
  | UpdateCampaigns
  | UpdateCampaignsFilterDate
  | UpdateFunderOrgConfig
  | UpdateUserConfig
  | UpdateBoxToken
  | UpdateNonprofitLoading
  | UpdateFunderLoading
  | UpdateNonprofitErrored
  | UpdateFunderErrored
  | UpdateUserLoading
  | UpdateAppDrawer
  | UpdateAppPanel
  | UpdateFeatureGatingLimit
  | UpdateShowAcademyIntroSection
  | UpdateDismissedResources
  | UpdateAdblockerDetected
  | UpdateCloudinaryConfig
  | UpdateThirdPartyNonprofitOrgConfig
  | UpdateFeatureFlagsLoading
  | UpdateFeatureFlags
  | UpdateGlobalDialog<TrialEndPayload | UpgradeModalDialogPayload>
  | SetBlockDragging
  | SetStoryDetails
  | SetActiveSidePanel
  | SetTocVisibility
  | SetRedirectPath
  | SetAiStoryContent

export type UserConfig = {
  createdAt: string
  firstName: string
  lastName: string
  id: number
  email: string
  phone: string
  nonprofitExperienceRange: NonprofitExperienceRange | null
  permissions: string[]
  loading: boolean
  lastLoggedInAt?: string | null
}

export type FunderUserConfig = {
  createdAt: string
  firstName: string
  lastName: string
  id: number
  email: string
  phone: string
  role: string
  funderOrgRole: string
  permissions: string[]
  loading: boolean
  lastLoggedInAt?: string | null
}

export type TokenData = {
  accessToken: string
  expiresIn: string
  acquiredAt: string
}

export type Address = {
  description: string
  structured_formatting: JSON
}

export type NonprofitOrgConfig = {
  orgName: string
  orgId: number
  legalName: string
  role: 'ADMIN' | 'STAFF' | 'OWNER'
  formationPlan: FormationPlan | null
  platformPlan: PlatformPlan | null
  loading: boolean
  errored: boolean
  permissions: string[]
  logoBoxId: string | null
  logoCloudinaryFileId: string | null
  websiteUrl: string | null
  nonprofitOrgRole: NonprofitOrgRole | null
  hasTaxExemptStatusAtSignup: boolean | null
  ein: string | null
  paidStaffSizeRange: PaidStaffSizeRange | null
  volunteerSizeRange: VolunteerSizeRange | null
  isFiscallySponsored: boolean | null
  isBipocLed: boolean | null
  isBipocServing: boolean | null
  yearEstablished: string | null
  address: Address | GooglePlaceApiPayload | null
  stripeConnectId: string
  isStripeConnectActive: boolean
  is501c3Verified: boolean
  taxExemptionStatus: string
  stripeConnectIssues: StripeConnectIssues
  featureFlags: string[]
  numBoardMembers: string | null
  numTeamMembers: number | null
  donationPortalId: string | null
  donationPortalHashedId: string | null
  isFounder: boolean | null
  donationFeePercent: number | null
  missionStatement: string | null
  platformPlanAddOns: string[]
  funderOrgId: number | null
  activeFunders: number[]
}

export type NonprofitDonationDetails = {
  hasRecurringAccess: boolean
  availableFrequencies: DonationInterval[]
  state: NonprofitOrgDonationsState
  loading: boolean
}

export type ThirdPartyNonprofitOrgConfig = {
  nonprofitOrgId: number
  stripeCustomerId: string
}

export type StripeConnectIssues = {
  resumeKYCUrl: string | null
  errors: string[]
  pendingVerification: string[]
  missingBusinessUrl: boolean
}

export type SponsoredNonprofit = { nonprofitOrgId: number; name: string | null }
export type SponsoredNonprofits = Record<string, SponsoredNonprofit>

export type FunderOrgConfig = {
  sponsoredNonprofits: SponsoredNonprofits
  orgName: string
  orgId: number
  loading: boolean
  errored: boolean
  user: FunderUserConfig
}

export type BoxConfig = {
  boxId: string
  tokenData: TokenData
}

export type CloudinaryConfig = {
  cloudinaryId: string | null
}

export type FeatureFlags = {
  activeFeatureFlags: string[]
  loading: boolean
}

type FeatureGatingLimit = {
  featureName: string
  lowestPlanTier: string
  visible: boolean
  limit: number
}

export type Dispatch = (action: Action) => void

export type State = {
  ui: {
    appBar: {
      linearProgress: number
      displayLinearProgress: boolean
      header: {
        text: string
        iconName: string
      }
    }
    dialog: TrialEndPayload | UpgradeModalDialogPayload
    showNavigation: boolean
    appPanel: boolean
    featureGatingLimit: FeatureGatingLimit[] | null
    academy: {
      showAcademyIntroSection: boolean
      dismissedResources: ResourceFromSDK[]
    }
    adblockerDetected: boolean
  }
  donationDetails: NonprofitDonationDetails
  donations: {
    campaigns: { data: Campaign[]; isLoading: boolean }
  }
  storyBuilder: {
    ui: {
      isBlockDragging: boolean
      activeSidePanel: 'NONE' | 'SETTINGS' | 'IMAGE' | 'PROGRESS_REPORT'
      isTocVisible: boolean
    }
    story: {
      isSharedWithFunder?: boolean
      showHeader?: boolean
      showTableOfContents?: boolean
      title?: string
      socialDescription?: string
      socialTitle?: string
      colorTheme?: ColorTheme
      id?: number
      hashedId?: string
    }
  }
  aiStory: {
    blocks: AiBlocksUnion[] | undefined | null
    template: AssetType[] | undefined | null
  }
  filterDate: number | null
  nonprofitOrgConfig: NonprofitOrgConfig
  funderOrgConfig: FunderOrgConfig
  userConfig: UserConfig
  boxConfig: BoxConfig
  cloudinaryConfig: CloudinaryConfig
  awsFeatureFlags: FeatureFlags
  redirectPath: string
}

export type AppProviderProps = { children: React.ReactNode }
